import z from 'zod';
import compact from 'just-compact';
import transCore from 'next-translate/transCore';
import { toPlainText } from '@portabletext/toolkit';
import {
  event,
  page,
  researchProject,
  researchGroup,
  simpleBlockContent,
  article
} from '@stereoagency/sanity-schema';
import { Metadata } from 'next';
import { PageType, defaultLanguage } from '@stereoagency/constants';
import { $globalSanityClient, $globalSanityImageBuilder } from '@/sanity';
import { extractPageFromPathname, formatProfileName } from '@/utils';
import { i18nvalueSubqueryFactory } from '@/sanity/helpers';
import fetchTranslationMetadataForSlugAndLang from './fetchTranslationMetadataForSlugAndLang';
import fetchAndUseI18n from './fetchAndUseI18n';

const pageQuery = page.schema.builder
  .filter('_id == $id')
  .pick(['_id', 'title', 'seo'])
  .first()
  .use();

const pageFallbackQuery = page.schema.builder
  .filter(
    `($slug == "/" && pageType == "${PageType.HOMEPAGE}") || slug.current == $slug`
  )
  .pick(['_id', 'title', 'seo'])
  .first()
  .use();

const researchProjectQuery = researchProject.schema.builder
  .filter('slug.current == $slug')
  .pick(['datetime'])
  .map((resolver) => ({
    author: resolver.author.resolve(['name', 'firstName']).use()
  }))
  .subquery({
    title: i18nvalueSubqueryFactory<string>('title'),
    introduction: i18nvalueSubqueryFactory<z.infer<
      typeof simpleBlockContent.validator
    > | null>('introduction')
  })
  .first()
  .use();

const eventQuery = event.schema.builder
  .filter('slug.current == $slug')
  .pick(['datetime', 'organizer'])
  .subquery({
    title: i18nvalueSubqueryFactory<string>('title'),
    introduction: i18nvalueSubqueryFactory<z.infer<
      typeof simpleBlockContent.validator
    > | null>('introduction')
  })
  .first()
  .use();

const articleQuery = article.schema.builder
  .filter('slug.current == $slug')
  .pick(['datetime', 'thumbnail'])
  .subquery({
    title: i18nvalueSubqueryFactory<string>('title'),
    introduction: i18nvalueSubqueryFactory<z.infer<
      typeof simpleBlockContent.validator
    > | null>('introduction')
  })
  .first()
  .use();

const researchGroupQuery = researchGroup.schema.builder
  .filter('slug.current == $slug')
  .subquery({
    title: i18nvalueSubqueryFactory<string>('title'),
    introduction: i18nvalueSubqueryFactory<z.infer<
      typeof simpleBlockContent.validator
    > | null>('introduction')
  })
  .first()
  .use();

export default async function fetchMetadata(
  lang: string,
  slug: string[]
): Promise<Metadata> {
  const base = new URL(
    process.env.NEXT_PUBLIC_SEO_URL ?? 'https://www.deduveinstitute.be/'
  );
  const pageType = extractPageFromPathname(lang, slug.join('/'));
  const notFoundError = new Error(
    `Could not find object of type "${pageType.type}" for path "${lang}/${pageType.slug}"`
  );

  if (pageType.type === 'search') {
    const i18n = await fetchAndUseI18n(lang, 'search');
    const t = transCore({
      lang: i18n.lang,
      config: i18n.config,
      allNamespaces: i18n.namespaces,
      pluralRules: new Intl.PluralRules(i18n.lang)
    });
    return {
      title: t('search:title')
    };
  }

  if (pageType.type === 'page') {
    const metadata = await fetchTranslationMetadataForSlugAndLang(
      lang,
      pageType.slug
    );
    const pageId =
      compact(metadata?.translations ?? []).find(
        ({ language }) => language === lang
      )?._id ||
      compact(metadata?.translations ?? []).find(
        ({ language }) => language === defaultLanguage.id
      )?._id;

    const result =
      typeof pageId === 'string'
        ? await $globalSanityClient.fetch<(typeof pageQuery)[1]>(pageQuery[0], {
            id: pageId
          })
        : await $globalSanityClient.fetch<(typeof pageFallbackQuery)[1]>(
            pageFallbackQuery[0],
            { language: lang, slug: pageType.slug }
          );

    if (!result) {
      throw notFoundError;
    }

    return {
      metadataBase: base,
      title: result.seo?.seoTitle ?? result.title ?? '',
      description: result.seo?.metaDescription,
      keywords: compact([
        result.seo?.focusKeyphrase,
        result.seo?.keyphraseSynonyms
      ]).join(', '),
      robots: {
        index: !result.seo?.hidePageFromSearchEngines,
        follow: !result.seo?.hidePageFromSearchEngines
      },
      openGraph: {
        images: result.seo?.seoImage
          ? [
              {
                url: $globalSanityImageBuilder
                  .image(result.seo.seoImage)
                  .width(1200)
                  .height(600)
                  .fit('fill')
                  .url()
              }
            ]
          : []
      }
    };
  }

  if (pageType.type === 'article') {
    const result = await $globalSanityClient.fetch<(typeof articleQuery)[1]>(
      articleQuery[0],
      {
        slug: pageType.slug,
        language: lang,
        fallback: defaultLanguage.id
      }
    );

    if (!result) {
      throw notFoundError;
    }

    const description = toPlainText(result.introduction ?? []);

    return {
      metadataBase: base,
      title: result.title,
      description,
      openGraph: {
        type: 'article',
        publishedTime: result.datetime,
        images: result.thumbnail?.asset._ref
          ? [
              {
                url: $globalSanityImageBuilder
                  .image(result.thumbnail)
                  .width(1200)
                  .height(600)
                  .fit('fill')
                  .url()
              }
            ]
          : []
      }
    };
  }

  if (pageType.type === 'event') {
    const result = await $globalSanityClient.fetch<(typeof eventQuery)[1]>(
      eventQuery[0],
      {
        slug: pageType.slug,
        language: lang,
        fallback: defaultLanguage.id
      }
    );

    if (!result) {
      throw notFoundError;
    }

    const description = toPlainText(result.introduction ?? []);

    return {
      metadataBase: base,
      title: result.title,
      description,
      openGraph: {
        type: 'article',
        authors: compact([result.organizer]),
        publishedTime: result.datetime
      }
    };
  }

  if (pageType.type === 'researchGroup') {
    const result = await $globalSanityClient.fetch<
      (typeof researchGroupQuery)[1]
    >(researchGroupQuery[0], {
      slug: pageType.slug,
      language: lang,
      fallback: defaultLanguage.id
    });

    if (!result) {
      throw notFoundError;
    }

    const description = toPlainText(result.introduction ?? []);

    return {
      metadataBase: base,
      title: result.title,
      description
    };
  }

  if (pageType.type === 'researchProject') {
    const result = await $globalSanityClient.fetch<
      (typeof researchProjectQuery)[1]
    >(researchProjectQuery[0], {
      slug: pageType.slug,
      language: lang,
      fallback: defaultLanguage.id
    });

    if (!result) {
      throw notFoundError;
    }

    const description = toPlainText(result.introduction ?? []);

    return {
      metadataBase: base,
      title: result.title,
      description,
      openGraph: {
        type: 'article',
        authors: compact([
          result.author ? formatProfileName(result.author) : null
        ]),
        publishedTime: result.datetime
      }
    };
  }

  return {};
}
